import type { WorkExperience } from "../../models/WorkExperience";
import disneyPlusLogo from "../company-logos/disney-plus.jpg";
import huluLogo from "../company-logos/hulu.svg";
import notMeBrushLogo from "../company-logos/notme-brush.png";
import epitaLogo from "../company-logos/epita.png";
import kanopyLogo from "../company-logos/kanopy.svg";
import easyMovieLogo from "../company-logos/easymovie.jpg";

const workData: WorkExperience[] = [
  {
    dates: ["JUN 2026 — PRESENT"],
    role: "Senior Software Engineer",
    company: "Disney+",
    link: "https://disney.com/",
    parentCompany: "Disney",
    logo: disneyPlusLogo,
    logoClass: "disney-plus",
    location: "Santa Monica, CA",
    employment: "Full-time",
    detail: "Building the Disney+ TV app client on the Browser Based Device (BBD) team for Samsung Tizen and LG webOS.",
    highlights: [
      "Build the Disney+ TV app client for Samsung Tizen and LG webOS.",
      "Develop React and TypeScript interfaces for the living-room experience.",
      "Work across JavaScript, HTML, and CSS to support the client on both TV platforms."
    ],
    tools: ["React", "TypeScript", "Samsung Tizen", "LG webOS", "JavaScript", "HTML / CSS"],
    story: [{ children: <>
      <p>In June 2026, I moved from Hulu to Disney+ within Disney Streaming. I am a Senior Software Engineer on the Browser Based Device (BBD) team, which works on the TV app client. For Disney+, I focus on Samsung Tizen and LG webOS.</p>
      <p>My work is on the client experience: building React and TypeScript interfaces, working through the differences between the two TV platforms, and using JavaScript, HTML, and CSS to make the app feel consistent on the big screen.</p>
    </> }]
  },
  {
    dates: ["SEP 2024 — JUN 2026"],
    role: "Senior Software Engineer",
    company: "Hulu",
    link: "https://www.hulu.com/",
    parentCompany: "Disney",
    logo: huluLogo,
    logoClass: "hulu",
    location: "Santa Monica, CA",
    employment: "Full-time",
    detail: "Built Hulu's TV app client on the BBD team across Samsung Tizen, LG webOS, Android TV, and Fire TV.",
    highlights: [
      "Worked on the Hulu TV app client for Samsung Tizen, LG webOS, Android TV, and Fire TV.",
      "Built React and TypeScript interfaces for a living-room experience across several device ecosystems.",
      "Adapted front-end behavior across TV platforms using JavaScript, HTML, and CSS."
    ],
    tools: ["React", "TypeScript", "Samsung Tizen", "LG webOS", "Android TV", "Fire TV", "JavaScript", "HTML / CSS"],
    story: [{ children: <>
      <p>From September 2024 to June 2026, I worked on Hulu as a Senior Software Engineer on Disney Streaming's Browser Based Device (BBD) team. The team works on the TV app client, and my Hulu work covered Samsung Tizen, LG webOS, Android TV, and Fire TV.</p>
      <p>I built front-end interfaces with React and TypeScript, and worked through the platform differences involved in bringing the Hulu experience to those devices. The role combined shared client development with the JavaScript, HTML, and CSS details of each TV platform.</p>
    </> }]
  },
  {
    dates: ["MAR 2019 — AUG 2024"],
    role: "Chief Product Officer",
    company: "NotMe Solutions",
    link: "https://not-me.com/",
    logo: notMeBrushLogo,
    logoClass: "notme",
    location: "Los Angeles, CA",
    employment: "Full-time",
    detail: "Built a misconduct-reporting product across mobile and web, and led a team of five.",
    highlights: ["Designed the product architecture and built the first iOS app in Swift.", "Led the React Native app, Vue admin dashboard and analytics, and React PWA.", "Designed the UI in Sketch and Figma; added notifications, analytics, and telemetry with Firebase and Sentry.", "Led a five-person team, introduced Scrum sprints and Jest testing, and oversaw product quality."],
    tools: ["React Native", "React", "Vue", "Swift", "Jest", "Figma"],
    story: [
      {
        children: (
          <>
            <p>
              NotMe Solutions is a tech startup specializing in workplace
              solutions, offering products like a misconduct reporting app and
              an analytics dashboard. As a founding member, I played a crucial
              role, leading project management and making key technology
              decisions.
            </p>
            <p>
              I designed and developed the product architecture, building the
              first iOS app in Swift. Additionally, I led the development of a
              cross-platform React Native version of the app. Furthermore, I
              spearheaded the development of the admin web dashboard and
              analytics using Vue. I also created a PWA version of the mobile
              app in React. Implementing notifications, analytics, and telemetry
              using Firebase and Sentry, I ensured seamless functionality.
              Moreover, I designed the entire UI/UX of the projects using Sketch
              and Figma.
            </p>
            <p>
              Leading a team of 5, I oversaw project development, ensured team
              coordination, and acted as the primary communicator with other
              teams for collaboration. I established the Scrum and sprint
              processes as the team scaled up. Additionally, I implemented a
              comprehensive testing process using Jest. While supervising
              testing and validation procedures, I maintained code quality,
              conducted code analysis, performed UI/UX testing, and conducted
              process reviews.
            </p>
          </>
        ),
      },
    ]
  },
  {
    dates: ["FEB — JUL 2017", "JAN 2018 — FEB 2019"],
    role: "iOS Developer",
    company: "EPITA Innovation Lab",
    logo: epitaLogo,
    logoClass: "epita",
    location: "Paris, France",
    employment: "Full-time · Lab",
    detail: "Built Epilife for IONIS students, taught iOS at EPITA, and explored ARKit.",
    highlights: ["Worked on Windchat, a Snapchat-style Swift app with camera, friends, and messaging features.", "Designed and built Epilife, the course scheduling app for IONIS students.", "Taught iOS to third-year EPITA students, creating exercises and leading practical sessions.", "Prototyped ARKit control for Hue lights and an interactive poster with a supporting web platform."],
    tools: ["Swift", "ARKit", "Angular"],
    story: [
      {
        children: (
          <p>
            I joined the Innovation Institute of EPITA after a rigorous
            recruitment process, competing with hundreds of students from the
            school. During this time, I worked on a Snapchat-like project named
            Windchat. The goal of the project was to develop a Snapchat-like app
            from scratch on iOS using Swift. This project aimed to test our
            ability to create a complete application with an authentication
            layer and all the basic features of Snapchat, including taking
            pictures using AVCaptureSession, adding text to images, managing
            friends, and sending and receiving images.
          </p>
        ),
      },
      {
        children: (
          <p>
            In the laboratory, my role was to work as an iOS/Swift developer,
            learning all the business processes involved in application
            development. The first project we undertook was the complete design
            and implementation of <b>Epilife</b>, the scheduling application
            used by students of the IONIS group to view their courses.
          </p>
        ),
      },
      {
        children: (
          <p>
            For two years, I provided iOS courses to third-year students at
            EPITA. During the first year, I worked as an assistant, and in the
            second year, I took on the role of instructor. My responsibilities
            included conducting practical sessions, facilitating the learning
            process, creating exercises, and assisting students.
          </p>
        ),
      },
      {
        children: (
          <p>
            I worked on an iOS application to control HUE lights using ARKit.
            Additionally, I developed an iOS application to interact with a
            poster using ARKit, along with a web platform to control all data
            displayed on top of the poster. This project was showcased during
            the school's open day to demonstrate current and previous projects
            of the laboratory.
          </p>
        ),
      },
      {
        children: (
          <p>
            During this time, I learned how to interact with a team and
            collaborate with multiple departments in a company while still being
            in school. Additionally, I acquired skills in project architecture
            and gained a better understanding of working on proof of concepts
            and technology exploration. In my final months in the laboratory, I
            also worked as an assistant to onboard new students through the
            laboratory processes.
          </p>
        ),
      },
    ]
  },
  {
    dates: ["AUG — DEC 2017"],
    role: "iOS / tvOS Developer Intern",
    company: "Kanopy",
    link: "https://www.kanopy.com/",
    logo: kanopyLogo,
    logoClass: "kanopy",
    location: "San Francisco, CA",
    employment: "Full-time internship",
    detail: "Adapted Kanopy's iOS app for Apple TV; it reached 2,700+ unique users during my internship.",
    highlights: ["Shared code between the iOS and tvOS apps while adapting the experience for Apple TV.", "Worked with a designer on Apple's Human Interface Guidelines and playback with AVPlayer and m3u8 streams.", "Shipped the app to the App Store after team testing; it reached 2,700+ unique users by the end of the internship."],
    tools: ["Swift", "tvOS", "AVPlayer", "m3u8"],
    story: [
      {
        // date: "Aug. 2017 - Sep. 2017",
        children: (
          <>
            <p>
              Worked to adapt the existing company iOS app to tvOS as an
              extension. Collaborated with a code-sharing mechanism to
              streamline development and maintain consistency across both iOS
              and tvOS.
            </p>
            <p>
              Collaboration was integral, and I closely collaborated with a
              designer to ensure the app adhered to Apple's Human Interface
              Guidelines, guaranteeing a seamless and user-friendly experience
              for our audience. This endeavor marked my first time developing
              for the Apple TV platform and provided a tremendous learning
              opportunity. I acquired valuable expertise in working with
              AVPlayer and handling the m3u8 format, enriching my understanding
              of media playback and content delivery on this platform.
            </p>
            <p>
              After weeks of testing conducted by the entire team, the
              application was successfully launched on the App Store, marking a
              significant milestone for our team. By the end of my internship,
              the app had been used by over 2,700 unique users.
            </p>
          </>
        ),
      },
    ]
  },
  {
    dates: ["JUN — AUG 2016"],
    role: "iOS Developer Intern",
    company: "EasyMovie",
    link: "https://www.easy.movie/",
    logo: easyMovieLogo,
    logoClass: "easymovie",
    location: "Paris, France",
    employment: "Full-time internship",
    detail: "Improved a Swift video-creation app, from API integration to drag-and-drop interactions.",
    highlights: ["Connected the iOS app to its API with Alamofire.", "Implemented drag-and-drop interactions to make video creation easier."],
    tools: ["Swift", "Alamofire", "iOS"],
    story: [
      {
        children: (
          <p>
            I worked with a small team on a brand new project at EasyMovie, a
            startup focused on simplifying the video creation process for
            businesses and individuals. Their platform offers user-friendly
            tools and features, enabling users to easily create, edit, and
            produce professional-quality videos without the need for extensive
            technical expertise.
          </p>
        ),
      },
      {
        children: (
          <p>
            During the internship, the focus was on understanding the project's
            challenges and requirements, as well as learning Swift 2 for iOS
            development. The main task involved ensuring the smooth
            functionality of the existing iOS application with an API. This was
            achieved by utilizing the Alamofire library, which facilitated
            effective communication between the project and the API.
          </p>
        ),
      },
      {
        children: (
          <p>
            I worked on implementing drag and drop functionality in the
            application to enhance the overall user experience. Throughout the
            internship, I not only deepened my technical expertise but also made
            significant contributions to improving the project's usability and
            overall user experience.
          </p>
        ),
      },
    ]
  }
];

export default workData;
